//@flow

import StringUtils from 'common/utils/StringUtils';
import DataUtils from 'common/utils/DataUtils';
import TimeUtils, { DateTimeFormat } from 'common/utils/TimeUtils';
import { sumBy } from 'common/utils/ImmutableUtils';
import { VisitStatusEnum, PodTypeEnums } from 'common/enums/VisitEnums';
import _ from 'lodash';

import I18nHelper from 'common/helpers/I18nHelper';
import type MinimizedTourVisit from 'common/models/MinimizedTourVisit';
import type { List } from 'immutable';
import { neoUIColors } from 'common/enums/ColorEnum';
import type MinimizedVehicleTour from 'common/models/MinimizedVehicleTour';
import {
    AllocatedTasks,
    Visits as AllocateVisits
} from 'common/models/internal/PlanAllocated';
import {
    UnallocatedTasks,
    Visits as UnallocateVisits
} from 'common/models/internal/PlanUnallocated';
import {
    type TourVisitIdentifierType,
    type VisitIdentifierType
} from 'common/contexts/VisitStateContext';
import EnvConfig from 'config';
import type { VisitIndexReferenceType } from 'common/types/common.types';

const translationsNamespace = ['liveView'];
const nsTranslate = I18nHelper.getTranslate(translationsNamespace);

export class VisitUtils {
    static GLOBAL_PERSONNEL_CLIENT_ID = 'mara';
    static OBFUSCATED_GLOBAL_PERSONNEL_TEXT = 'Locus';

    static getResourceValue(visit, resourceName) {
        const resources = visit.get ? visit.get('resources') : visit.resources;
        const resource = resources.find(i => {
            const res = i.get ? i.get('name') : i.name;
            return res === resourceName;
        });
        if (resource) {
            return resource.get ? resource.get('value') : resource.value;
        }
        return '';
    }

    static getTransactionStatusLabel({
        visit,
        totalQuantity,
        totalTransactedQuantity,
        placeholder = '-'
    }: {
        totalQuantity?: number,
        totalTransactedQuantity?: ?number,
        placeholder?: ?string
    }) {
        let visitStatus = '';
        let totalItemQuantity = totalQuantity;
        let totalTransactedItemQuantity = totalTransactedQuantity;
        if (visit.get) {
            visitStatus =
                visit.get('visitStatus') || visit.get('effectiveStatus');
            const lineItems = visit.getIn(['orderDetail', 'lineItems']);
            totalTransactedItemQuantity = VisitUtils.getTotalTransactedQuantity(
                lineItems
            );
            totalItemQuantity = VisitUtils.getTotalQuantity(lineItems);
        } else {
            visitStatus = _.get(visit, 'visitStatus');
        }
        // hack, passing quantity from outside sometimes
        return VisitUtils.getSingleTransactionStatus(
            visitStatus,
            totalItemQuantity,
            totalTransactedItemQuantity,
            placeholder
        );
    }

    static getSingleTransactionStatus(
        visitStatus: string,
        quantityBooked: ?number,
        quantityTransacted: ?number,
        placeholder: string
    ) {
        if (
            visitStatus === VisitStatusEnum.Completed ||
            visitStatus === VisitStatusEnum.Cancelled
        ) {
            if (
                StringUtils.isEmpty(quantityBooked) ||
                StringUtils.isEmpty(quantityTransacted)
            ) {
                return placeholder;
            } else if (quantityBooked === quantityTransacted) {
                return nsTranslate('all');
            } else if (quantityTransacted === 0) {
                return nsTranslate('translations:none');
            } else if (quantityTransacted < quantityBooked) {
                return 'Partial';
            }
        }
        return placeholder;
    }
    static formatIDs(str, obfuscate = true) {
        const regexPattern = /([^/]+)\/personnel\/([^/]+)?$/g;
        return _.replace(str, regexPattern, (match, clientId, personnelId) => {
            const formattedId =
                obfuscate && clientId === VisitUtils.GLOBAL_PERSONNEL_CLIENT_ID
                    ? VisitUtils.OBFUSCATED_GLOBAL_PERSONNEL_TEXT
                    : `Dashboard - ${personnelId}`;
            return formattedId;
        });
    }

    static formatRiderOrPersonnelIds(str, obfuscate = true) {
        const riderIdPattern = /^([^/]+)\/([^/]+)$/;
        if (riderIdPattern.test(str)) {
            return _.replace(
                str,
                riderIdPattern,
                (match, clientId, riderId) => {
                    return `${nsTranslate(
                        'assignedByStatus.rider'
                    )} - ${riderId}`;
                }
            );
        }
        return VisitUtils.formatIDs(str, obfuscate);
    }
    static getTotalQuantity(lineItems) {
        return lineItems && lineItems.size > 0
            ? sumBy(lineItems, 'quantity')
            : null;
    }
    static getTotalTransactedQuantity(lineItems) {
        return lineItems && lineItems.size > 0
            ? sumBy(lineItems, ['transactionStatus', 'transactedQuantity'])
            : null;
    }

    static filterUnfilledItems = items => {
        return items?.filter(item => {
            const selectedValue =
                item.selectedValue || item.get('selectedValue'); //Fallback, item is a map in v1
            if (!selectedValue && !DataUtils.isBoolean(selectedValue)) {
                return false;
            }
            return true;
        });
    };

    static formatDateTimeValue(selectedValue: string, type: string) {
        switch (type) {
            case PodTypeEnums.Date: {
                const parsedDate = selectedValue
                    ? TimeUtils.parseDateTime(
                          selectedValue,
                          DateTimeFormat.LONG_DATE_FORMAT
                      )
                    : selectedValue;
                return TimeUtils.format(
                    parsedDate,
                    DateTimeFormat.FULL_FANCY_DATE_FORMAT
                );
            }
            case PodTypeEnums.Time: {
                const parsedTime = selectedValue
                    ? TimeUtils.parseDateTime(
                          selectedValue,
                          DateTimeFormat.MILITARY_TIME_FORMAT
                      )
                    : selectedValue;
                return TimeUtils.format(
                    parsedTime,
                    DateTimeFormat.FANCY_TIME_FORMAT
                );
            }
            case PodTypeEnums.DateTime:
            default: {
                const parsedDateTime = selectedValue
                    ? TimeUtils.parseDateTime(
                          selectedValue,
                          DateTimeFormat.SHORT_ISO_FORMAT
                      )
                    : selectedValue;
                const { date, time } = TimeUtils.formatDateTime({
                    date: parsedDateTime,
                    dateFormat: DateTimeFormat.FULL_FANCY_DATE_FORMAT,
                    timeFormat: DateTimeFormat.FANCY_TIME_FORMAT
                });
                return `${date}, ${time}`;
            }
        }
    }

    static mergeResources(resources, mergedVisits) {
        if (!mergedVisits) {
            return resources;
        }
        const mergedResources = resources.map(resource => {
            const resourceValue = mergedVisits.reduce((value, visit) => {
                if (!visit.resources && !visit.resources.length) {
                    return value;
                }
                const visitResource = visit.resources
                    .filter(res => res.name === resource.name)
                    .get(0);

                if (
                    !visitResource ||
                    visitResource.exchangeType !== resource.exchangeType
                ) {
                    return value;
                }
                return visitResource.value + value;
            }, resource.value);
            if (resourceValue) {
                return resource.setValue(resourceValue);
            }
            return resource;
        });
        return mergedResources;
    }

    static areVisitIndexesEqual(
        visitIndexRef1: VisitIndexReferenceType | MinimizedTourVisit,
        visitIndexRef2: VisitIndexReferenceType | MinimizedTourVisit
    ): boolean {
        if (
            !DataUtils.isEmpty(visitIndexRef1) &&
            !DataUtils.isEmpty(visitIndexRef2)
        ) {
            return (
                visitIndexRef1.id === visitIndexRef2.id &&
                (visitIndexRef1.sourceId === visitIndexRef2.sourceId ||
                    (visitIndexRef1.mergedVisitSourceId || '').includes(
                        visitIndexRef2.sourceId
                    ) ||
                    (visitIndexRef2.mergedVisitSourceId || '').includes(
                        visitIndexRef1.sourceId
                    ) ||
                    visitIndexRef1.mergedVisitSourceIds?.includes(
                        visitIndexRef2.sourceId
                    ) ||
                    visitIndexRef2.mergedVisitSourceIds?.includes(
                        visitIndexRef1.sourceId
                    )) &&
                visitIndexRef1.transactionType ===
                    visitIndexRef2.transactionType
            );
        }
        return false;
    }

    static getVisitByIndex(
        visits: List<MinimizedTourVisit>,
        visitIndexRef: VisitIndexReferenceType
    ): ?MinimizedTourVisit {
        if (visitIndexRef !== null) {
            return visits
                .filter(visit => {
                    return VisitUtils.areVisitIndexesEqual(
                        visit,
                        visitIndexRef
                    );
                })
                .get(0);
        }
    }

    // @DEPRECATED
    static getVisitColor(status: $Values<typeof VisitStatusEnum>) {
        switch (status) {
            case VisitStatusEnum.Waiting:
                return neoUIColors.grey400;
            case VisitStatusEnum.Queued:
                return neoUIColors.grey600;
            case VisitStatusEnum.Travelling:
            case VisitStatusEnum.Transacting:
                return neoUIColors.blue400;
            case VisitStatusEnum.Completed:
                return neoUIColors.green500;
            case VisitStatusEnum.Cancelled:
                return neoUIColors.red400;
        }
        throw new Error(`Unknown visit status: ${status}`);
    }

    static getVisitColorFromNeoUI(
        status: $Values<typeof VisitStatusEnum>
    ): string {
        switch (status) {
            case VisitStatusEnum.Waiting:
                return 'grey.400';
            case VisitStatusEnum.Queued:
                return 'grey.600';
            case VisitStatusEnum.Travelling:
            case VisitStatusEnum.Transacting:
                return 'blue.400';
            case VisitStatusEnum.Completed:
                return 'green.500';
            case VisitStatusEnum.Cancelled:
                return 'red.400';
            default:
                return 'grey.400';
        }
    }

    static getAggregatedVolume(visit: MinimizedTourVisit): number {
        const { mergedVisits } = visit;
        const sum = visit.getVolumeValue();
        return mergedVisits.reduce(
            (sum, visit) => sum + visit.getVolumeValue(),
            sum
        );
    }

    static getSlot(
        visit: MinimizedTourVisit
    ): { startTime: string, endTime: string } {
        const { mergedVisits } = visit;
        const slotStartTime = visit.chosenLocation.slot.startTime;
        let slotEndTime = visit.chosenLocation.slot.endTime;
        if (mergedVisits && !mergedVisits.isEmpty()) {
            const lastVisit = mergedVisits.last();
            slotEndTime = lastVisit.chosenLocation.slot.endTime;
        }
        return { startTime: slotStartTime, endTime: slotEndTime };
    }

    static getEtd(visit: MinimizedTourVisit): string {
        let { currentEtd } = visit;
        const { mergedVisits } = visit;
        if (mergedVisits && !mergedVisits.isEmpty()) {
            const lastVisit = mergedVisits.last();
            ({ currentEtd } = lastVisit);
        }
        return currentEtd;
    }

    static getTransactionDuration(visit: MinimizedTourVisit): number {
        const { currentEtt, chosenLocation } = visit;
        const { slot } = chosenLocation;
        const { transactionDuration } = slot;
        const currentEtd = VisitUtils.getEtd(visit);
        let tat = transactionDuration;
        if (currentEtt && currentEtd) {
            tat = TimeUtils.diff({
                startDate: currentEtt,
                endDate: currentEtd
            });
        }
        return tat;
    }

    static getVisitFromTour(
        tour: MinimizedVehicleTour,
        visitId: VisitIdentifierType
    ): ?MinimizedTourVisit {
        return tour.visits.find(visit => {
            return (
                visit.id === visitId.id && visit.sourceId === visitId.sourceId
            );
        });
    }

    static getVisitByIdentifier(
        tours: List<MinimizedVehicleTour>,
        tourVisitIdentifier: TourVisitIdentifierType
    ): ?MinimizedTourVisit {
        const tour = tours.find(
            tour => tour.tourId.tourId === tourVisitIdentifier.tourId
        );

        if (!tour) {
            return null;
        }

        const { visitId } = tourVisitIdentifier;
        return VisitUtils.getVisitFromTour(tour, visitId);
    }

    static areVisitIdentifiersEqual(
        visitIdentifier1: ?VisitIdentifierType,
        visitIdentifier2: ?VisitIdentifierType
    ) {
        if (
            !DataUtils.isEmpty(visitIdentifier1) &&
            !DataUtils.isEmpty(visitIdentifier1)
        ) {
            return (
                visitIdentifier1.id === visitIdentifier2.id &&
                visitIdentifier1.sourceId === visitIdentifier2.sourceId &&
                visitIdentifier1.transactionType ===
                    visitIdentifier2.transactionType
            );
        }
        return false;
    }

    static getVisitFromTaskList(
        taskList: List<AllocatedTasks> | List<UnallocatedTasks>,
        tourVisitIdentifier: TourVisitIdentifierType
    ): ?(AllocateVisits | UnallocateVisits) {
        const { visitId } = tourVisitIdentifier;
        const task = taskList.find(task => {
            const customerVisit = task.getCustomerVisit();
            return (
                customerVisit?.id === visitId.id &&
                customerVisit?.sourceId === visitId.sourceId
            );
        });

        return task?.getCustomerVisit();
    }

    static getHashKeyHomebaseActivityLogs(visit: MinimizedTourVisit): string {
        const {
            chosenLocation: { geometry },
            currentEtd,
            currentEta,
            visitStatus,
            locationId
        } = visit;
        const { lat, lng } = geometry?.latLng || {};
        const etByVisitStatus = visit.isTerminal() ? currentEtd : currentEta;
        const uniqueLocationKey =
            locationId?.locationId || `${lat?.toString()}_${lng?.toString()}`;
        return `${uniqueLocationKey}_${TimeUtils.format(
            etByVisitStatus,
            DateTimeFormat.SHEET_DATE_FORMAT_WITH_TIME
        )}_${visitStatus}`;
    }

    static getHashKeyHomebaseTimeline(visit: MinimizedTourVisit): string {
        const {
            chosenLocation: {
                geometry: {
                    latLng: { lat, lng }
                }
            },
            visitStatus,
            locationId
        } = visit;
        const uniqueLocationKey =
            locationId?.locationId || `${lat?.toString()}_${lng?.toString()}`;
        const eta = TimeUtils.format(
            visit.currentEta,
            DateTimeFormat.SHEET_DATE_FORMAT_WITH_TIME
        );
        const etd = TimeUtils.format(
            visit.currentEtd,
            DateTimeFormat.SHEET_DATE_FORMAT_WITH_TIME
        );

        return `${uniqueLocationKey}_${eta}_${etd}_${visitStatus}`;
    }

    static getHashKeyHomebaseMap(visit: MinimizedTourVisit): string {
        return VisitUtils.getHashKeyHomebaseActivityLogs(visit);
    }

    static getFormattedTimeRange(startDate: string, endDate: string): string {
        const timeFormat =
            TimeUtils.isToday(startDate) && TimeUtils.isToday(endDate)
                ? DateTimeFormat.FANCY_TIME_FORMAT
                : DateTimeFormat.HUMAN_DATE_TIME_FORMAT;
        const start = TimeUtils.format(startDate, timeFormat);
        const end = TimeUtils.format(endDate, timeFormat);
        return `${start} - ${end}`;
    }

    static getFormattedDateTimeRange({
        start,
        end
    }: {
        start: { date: string, time: string },
        end: { date: string, time: string }
    }): string {
        const { date: startDate, time: startTime } = start;
        const { date: endDate, time: endTime } = end;
        const startMoment = TimeUtils.getMoment(
            `${startDate} ${startTime}`,
            DateTimeFormat.LONG_DATE_WITH_TIME
        );
        const endMoment = TimeUtils.getMoment(
            `${endDate} ${endTime}`,
            DateTimeFormat.LONG_DATE_WITH_TIME
        );

        const timeFormat =
            TimeUtils.isToday(startMoment) && TimeUtils.isToday(endMoment)
                ? DateTimeFormat.FANCY_TIME_FORMAT
                : DateTimeFormat.HUMAN_DATE_TIME_FORMAT;
        const startString = TimeUtils.format(startMoment, timeFormat);
        const endString = TimeUtils.format(endMoment, timeFormat);
        return `${startString} - ${endString}`;
    }

    static getCustomViaShUrl(viaShUrl: string): string {
        const { customViaShHost } = EnvConfig;

        if (!customViaShHost) {
            return viaShUrl;
        }

        const viaShTripId = viaShUrl.split('/').pop();

        return `${customViaShHost}/${viaShTripId}`;
    }
}

export default VisitUtils;
